import _ from 'lodash';
import ReactApp from './global.react';
import {
  setUser,
  setRoom,
  setRooms,
  setPurchases,
  setPlans,
  setStates,
  setPersonalData,
  setLocationsLicensed,
  setUserManageableFeatures,
} from '../store/features/system/action-creators';
import {
  loadAndInstallExtensions,
  resetExtensionsState,
} from '../modules/extensions-framework/actions';
import { API, setUpApi } from '../vendor/api';
import { RootScopeEvents } from 'common/services/root-scope-types';
import { selectUser } from '../store/features/system';
import { localStorageKeyForRoomId } from '../modules/dashboard/components/invite-by-sms/constants';
import { verifySupportedLocale } from '../modules/core/helpers/verify-supported-locale';
import { PEOPLE_TYPES } from '../store/features/realtime/presence';
import {
  getActiveRooms,
  getLandingPage,
  hasGrowthBookFeature,
  GrowthBookFeatures,
} from '../modules/core';
import { actions } from '../modules/dokbot';

import {
  redirectToBlacklistPage,
  shouldRedirectToBlacklist,
} from '../modules/core/components/blacklisted-page/blacklisted-page-helper';
import {
  getGrowthbookFeatures,
  updateGrowthBookIndentifiers,
} from './global.experiments';
import wait from '../common/utils/wait';

export default angular.module('doxy.global.router', ['ui.router']).name;

angular
  .module('doxy.global.router')
  .config(function (
    $stateProvider,
    $urlRouterProvider,
    $locationProvider,
    $urlMatcherFactoryProvider
  ) {
    $urlMatcherFactoryProvider.caseInsensitive(true);

    /*@ngInject*/
    async function getCurrentInstitution(institutionHelper, UserLocationDetails) {
      return institutionHelper.getCurrentInstitutionNoCache().catch((err) => {
        const status = _.get(err, 'status');
        return Promise.reject(
          status !== -1 && status < 500 ? { target: 'wrongInstitution' } : {}
        );
      });
    }

    /*@ngInject*/
    function getRoomsMemberships(User, user) {
      //currentInstitution: getCurrentInstitution;
      return User.roomsMemberships({
        id: user.id,
        filter: {
          include: {
            relation: 'room',
            scope: {
              include: ['users', 'institution'],
              where: {
                institutionId: getCurrentInstitution.id,
              },
            },
          },
        },
      }).$promise;
    }

    const reactResolvers = {
      apiConfig(
        LoopBackAuth,
        apiUrl,
        lb4ApiUrl,
        Auth,
        notificationFactory,
        v2
      ) {
        'ngInject';
        // eslint-disable-next-line consistent-return
        const signOutHandler = () => {
          notificationFactory.showCommon(
            'warning',
            'Session expired: please log in again'
          );
          Auth.clearCurrentUser();

          if (Boolean(v2.isSignInEnabled())) {
            return v2.redirect('/sign-in');
          } else {
            history.pushState(null, '', '/sign-in');
          }
        };
        setUpApi({
          accessTokenId: LoopBackAuth.accessTokenId,
          apiUrl: apiUrl,
          lb4ApiUrl: lb4ApiUrl,
          signOut: signOutHandler,
        });
      },
    };
    $stateProvider
      .state('ssoAutoLogin', {
        url: '/sso-auto-login',
        access: {
          level: 'authenticated',
        },
        component: 'ssoAutoLogin',
      })
      .state('signIn', {
        url: '/sign-in?verify&l&r',
        data: {
          access: {
            level: 'unauthenticated',
            institution: true,
            wrongBrowser: true,
          },
        },
        resolve: {
          __gb: getGrowthbookFeatures,
          // eslint-disable-next-line consistent-return
          v2RedirectGuard(__gb, v2, $stateParams, $location) {
            if (Boolean(v2.isSignInEnabled())) {
              const queryParams = $location.search();

              // Referrer-based redirect after successful v2 sign-in
              if ($stateParams.r !== undefined) {
                queryParams.r = $stateParams.r;
              }

              return v2.redirect(
                '/sign-in',
                undefined,
                $stateParams.l,
                queryParams
              );
            }

            history.pushState(null, '', '/sign-in');

            if ($stateParams.r !== undefined) {
              $stateParams.r = undefined;
            }
          },
          apiConfig: reactResolvers.apiConfig,
          locale: ($translate, $stateParams) => {
            if ($stateParams.l) {
              $translate.use($stateParams.l);
            } else {
              const locale = verifySupportedLocale(
                PEOPLE_TYPES.doctor,
                navigator.language
              );

              $translate.use(locale);
            }
            return $translate.onReady();
          },
        },
        component: ReactApp,
      })
      .state('account.marketplace', {
        url: '/extensions',
        data: { title: 'Extensions' },
        resolve: reactResolvers,
        component: ReactApp,
      })
      .state('account.extension', {
        url: '/extension/:id',
        data: { title: 'Extension Details' },
        resolve: reactResolvers,
        component: ReactApp,
      })
      .state('account.analytics', {
        url: '/analytics',
        data: { title: 'Analytics' },
        resolve: reactResolvers,
        component: ReactApp,
      })
      .state('account.meeting-history', {
        url: '/meeting-history',
        data: { title: 'Meeting-History' },
        resolve: reactResolvers,
        component: ReactApp,
      });
    $stateProvider.state('isBlacklisted', {
      url: '/blacklisted',
      data: {
        access: {
          level: 'all',
        },
      },
      resolve: reactResolvers,
      component: ReactApp,
    });
    $stateProvider
      .state('blocked', {
        url: '/blocked',
        data: {
          access: {
            level: 'all',
          },
          prerenderStatusCode: '444',
        },
        resolve: reactResolvers,
        component: ReactApp,
      })
      .state('home', {
        url: '/?modal',
        component: 'institutionHome',
        data: {
          access: {
            level: 'unauthenticated',
            wrongBrowser: true,
          },
          bodyClass: 'center-content-horizontal',
        },
        resolve: {
          currentInstitution: getCurrentInstitution,
          rooms(routeCheck, Institution, currentInstitution) {
            return Institution.getRoomsForLandingPage({
              institutionId: currentInstitution.id,
            }).$promise;
          },
          routeCheck(currentInstitution, env) {
            // This route is tricky and is handled in a different way depending on circumstances.
            //
            // 1. It should show institution enter screen when served on a sub-domain.
            // 2. On doxy.me/doxi.me this same url is served by nginx as a static homepage from the marketing repo.
            //    However, if $state.go('home') is called from somewhere inside the app it will not actually redirect
            //    but rather will make a transition to this state.
            //    That's why it has to reload the page to have nginx handle it ("window.location" thing below).
            // 3. In development mode there's no nginx so it would just redirect to sing-in
            //    if it's not an institution url.
            //

            if (currentInstitution.isNull()) {
              if (env === 'development') {
                return Promise.reject({ target: 'signIn' });
              } else {
                window.location.href = '/';
                // Return a promise that never resolves
                // so no transition happens while page is reloading
                return new Promise(angular.noop);
              }
            }
          },
          institutionAuthState(
            currentInstitution,
            routeCheck,
            $timeout,
            $state,
            $stateParams,
            Auth
          ) {
            return Auth.checkInstitutionAuthState(currentInstitution).then(
              (token) => {
                token = token || {};
                if (token.id) {
                  $timeout(() =>
                    $state.go('account.dashboard', {
                      modal: $stateParams.modal || null,
                    })
                  );
                  return new Promise(angular.noop);
                }
                return token.id ? token : null;
              }
            );
          },
        },
        lazyLoad($transition$) {
          return import(
            '../_components/institution-home/institution-home'
          ).then(({ default: module }) => {
            $transition$.injector().native.loadNewModules([module]);
          });
        },
      })
      .state('wrongRoom', {
        url: '/wrong-room?{slug}{domain}',
        component: 'wrongRoom',
        data: {
          access: {
            level: 'all',
          },
          prerenderStatusCode: '404',
        },
        resolve: {
          currentInstitution: getCurrentInstitution,
          hintsResult: function (Room, $stateParams) {
            return new Promise((resolve) => {
              const params = {
                slug: $stateParams.slug,
                domain: $stateParams.domain,
                $_clearAuthorization: true,
              };

              Room.hints(params)
                .$promise.then(resolve)
                .catch((err) => {
                  console.error('wrongRoom Room.hints error:', err);
                  resolve({ hints: [] });
                });
            });
          },
        },
        lazyLoad($transition$) {
          return import('../home/wrong-room/wrong-room').then(
            ({ default: module }) => {
              $transition$.injector().native.loadNewModules([module]);
            }
          );
        },
      })
      .state('signUp', {
        url: '/sign-up?token&accountType&l',
        params: {
          token: { value: null, squash: true },
          accountType: { value: null, squash: true },
          l: null,
        },
        component: ReactApp,
        data: {
          access: {
            level: 'unauthenticated',
            wrongBrowser: true,
          },
          bodyClass: 'center-content-horizontal',
          title: 'home.title.sign-up',
          description: 'home.description.sign-up',
        },
        resolve: {
          __gb: getGrowthbookFeatures,
          async v2SignUpRedirectGuard(__gb, v2, $stateParams, $location) {
            // Use OG sign up page for clinic account sign ups or invite to clinics (indicated by token query param)
            if (
              $stateParams.accountType === 'clinic' ||
              Boolean($stateParams.token)
            ) {
              return;
            }

            const queryParams = $location.search();

            // If accountType is not defined, add accountType=pro to query params
            if ($stateParams.accountType == null) {
              queryParams.accountType = 'pro';
            }

            await v2.redirect(
              '/provider-selector',
              undefined,
              $stateParams.l,
              queryParams
            );
          },
          apiConfig: reactResolvers.apiConfig,
          currentInstitution: getCurrentInstitution,
          token: function ($stateParams, InviteToken, $q, $log) {
            if (!$stateParams.token) return;
            return InviteToken.findByToken({ token: $stateParams.token })
              .$promise.then(function (token) {
                if (token.used) throw new Error('Token ' + token.id + ' used');
                return token;
              })
              .catch(function (err) {
                $log.error('signUp.token', err);
                return $q.reject({
                  target: 'invite',
                  data: $stateParams,
                });
              });
          },
          plans: (Purchase, $ngRedux) => {
            return Purchase.getPlans().$promise.then((plans) => {
              $ngRedux.dispatch(setPlans(plans));
              return plans;
            });
          },
          locale: ($translate, $stateParams) => {
            if ($stateParams.l) {
              $translate.use($stateParams.l);
            } else {
              const locale = verifySupportedLocale(
                PEOPLE_TYPES.doctor,
                navigator.language
              );

              $translate.use(locale);
            }
            return $translate.onReady();
          },
        },
      })
      .state('paymentSetup', {
        url: '/payment-setup',
        component: ReactApp,
        data: {
          access: {
            level: 'unauthenticated',
            wrongBrowser: true,
          },
          bodyClass: 'center-content-horizontal',
          title: 'home.title.sign-up',
          description: 'home.description.sign-up',
        },
        resolve: {
          apiConfig: reactResolvers.apiConfig,
          plans: (Purchase, $ngRedux) => {
            return Purchase.getPlans().$promise.then((plans) => {
              $ngRedux.dispatch(setPlans(plans));
              return plans;
            });
          },
        },
      })
      .state('purchaseSuccess', {
        url: '/purchase-success',
        component: ReactApp,
        data: {
          access: {
            level: 'unauthenticated',
            wrongBrowser: true,
          },
          bodyClass: 'center-content-horizontal',
          title: 'home.title.sign-up',
          description: 'home.description.sign-up',
        },
        resolve: reactResolvers,
      })
      .state('forgotPassword', {
        url: '/forgot-password?l',
        component: 'forgotPassword',
        data: {
          access: {
            level: 'unauthenticated',
            wrongBrowser: true,
          },
          bodyClass: 'center-content-horizontal',
          title: 'Forgot Password',
        },
        resolve: {
          currentInstitution: getCurrentInstitution,
          locale: ($translate, $stateParams) => {
            if ($stateParams.l) {
              $translate.use($stateParams.l);
            } else {
              const locale = verifySupportedLocale(
                PEOPLE_TYPES.doctor,
                navigator.language
              );

              $translate.use(locale);
            }
            return $translate.onReady();
          },
          handleV2ForgotPasswordRedirect: function (v2, currentInstitution) {
            if (!Boolean(currentInstitution?.id)) {
              return v2.redirect('/forgot-password');
            }
            return Promise.resolve();
          },
        },
        lazyLoad($transition$) {
          return import('../auth/forgot-password/forgot-password').then(
            ({ default: module }) => {
              $transition$.injector().native.loadNewModules([module]);
            }
          );
        },
      })
      .state('resetPassword', {
        url: '/password-reset',
        component: 'resetPassword',
        data: {
          access: {
            level: 'unauthenticated',
            wrongBrowser: true,
          },
          bodyClass: 'center-content-horizontal',
          title: 'Reset Password',
        },
        resolve: {
          currentInstitution: getCurrentInstitution,
        },
        lazyLoad($transition$) {
          return import('../auth/reset-password/reset-password').then(
            ({ default: module }) => {
              $transition$.injector().native.loadNewModules([module]);
            }
          );
        },
      })
      .state('createRoom', {
        url: '/create-room',
        component: 'createRoom',
        data: {
          access: { level: 'authenticated', wrongBrowser: true },
          bodyClass: 'center-content-horizontal',
          title: 'Create Room',
        },
        resolve: {
          currentInstitution: getCurrentInstitution,
        },
        lazyLoad($transition$) {
          return import('../auth/create-room/create-room').then(
            ({ default: module }) => {
              $transition$.injector().native.loadNewModules([module]);
            }
          );
        },
      })
      .state('institutionLogin', {
        url: '/institution/login',
        resolve: {
          // Redirect everyone to the new institution homepage from this route
          redirectToHome: () => Promise.reject({ target: 'home' }),
        },
      })
      .state('wrongInstitution', {
        url: '/wrong-institution',
        component: 'wrongInstitution',
        data: {
          access: {
            level: 'all',
          },
          prerenderStatusCode: '404',
        },
        resolve: {
          hintsResult: async function (
            $state,
            Institution,
            routerHelperFactory,
            institutionHelper
          ) {
            const institution = await institutionHelper
              .getCurrentInstitution()
              .catch(() => null);

            if (institution) {
              $state.go('home');
              return Promise.reject(angular.noop);
            }

            const params = {
              domain: routerHelperFactory.getSubdomain(),
              $_clearAuthorization: true,
            };

            let hints;
            try {
              hints = await Institution.hints(params).$promise;
            } catch (err) {
              hints = { hints: [] };
            }

            return hints;
          },
        },
        lazyLoad($transition$) {
          return import('../home/wrong-institution/wrong-institution').then(
            ({ default: module }) => {
              $transition$.injector().native.loadNewModules([module]);
            }
          );
        },
      })
      .state('invite', {
        url: '/invite?{token}{accountType}',
        component: 'homeInvite',
        data: {
          access: {
            level: 'all',
          },
        },
        resolve: {
          currentInstitution: getCurrentInstitution,
          inviteTokenRes: function ($stateParams, $log, InviteToken) {
            if (!$stateParams.token) return {};
            return new Promise(function (resolve) {
              InviteToken.findByToken({
                token: $stateParams.token,
                $_clearAuthorization: true,
              })
                .$promise.then(resolve)
                .catch(function (err) {
                  resolve(err.data || {});
                });
            });
          },
        },
        lazyLoad($transition$) {
          return import('../home/invite/invite').then(({ default: module }) => {
            $transition$.injector().native.loadNewModules([module]);
          });
        },
      })
      .state('account', {
        url: '/account?vcp&l',
        component: 'account',
        abstract: true,
        data: {},
        params: {
          afterLogin: {
            value: false,
          },
          afterSignup: {
            value: false,
            squash: true,
          },
          vcp: null, // vcp = video chat provider (tokbox)
          l: null,
        },
        resolve: {
          apiConfig: reactResolvers.apiConfig,
          checkLocationDetails: async function (
            $stateParams,
            UserLocationDetails,
            $ngRedux
          ) {
            if (!$stateParams.afterLogin) {
              await UserLocationDetails.getLocationDetails();
            }
          },
          currentInstitution: getCurrentInstitution,
          institutionPurchaseCheck: (currentInstitution, $log) => {
            // Tracking for clinics that continue to use service without active subscription
            // For more details https://doxyme.atlassian.net/browse/CS-10563
            const isInstitution = Boolean(currentInstitution.id);
            if (!isInstitution) {
              return true;
            }
            const purchases = Boolean(currentInstitution.purchases)
              ? currentInstitution.purchases
              : [];
            const hasActivePurchase = purchases.some(
              (purchase) => purchase.isActive
            );
            if (hasActivePurchase === false) {
              $log.warn('Clinic without active purchase found', {
                id: currentInstitution.id,
                domain: currentInstitution.domain,
              });
            }
            return true;
          },
          accessService: function (
            $rootScope,
            user,
            currentInstitution,
            AccessService,
            extensionsService
          ) {
            const accessService = new AccessService(currentInstitution, user);
            currentInstitution.hasAccess =
              accessService.hasAccess.bind(accessService);
            currentInstitution.accessChanged = () => accessService.changed();
            currentInstitution.onAccessChange =
              accessService.onChanges.bind(accessService);
            user.hasAccess = accessService.hasAccess.bind(accessService);
            extensionsService.init(accessService);

            $rootScope.$on('updateToggleFeatures', () =>
              accessService.changed()
            );

            $rootScope.$on('subscription:upgradeConfirmed', () =>
              accessService.changed()
            );

            return accessService;
          },
          /* eslint-disable consistent-return */
          user: async (
            User,
            currentInstitution,
            LoopBackAuth,
            Auth,
            $state,
            $ngRedux
          ) => {
            const userPromise = User.findById({
              id: User.getCurrentId(),
              institutionId: currentInstitution.id,
              filter: {
                include: [
                  'betaAccesses',
                  'identities',
                  'capabilities',
                  {
                    institutionsMemberships: {
                      institution: ['purchases'],
                    },
                  },
                ],
              },
            }).$promise;

            const currentTimePromise = User.getCurrentTime().$promise.then(
              (result) => result.ts,
              () => Date.now()
            );

            try {
              const [user, time] = await Promise.all([
                userPromise,
                currentTimePromise,
              ]);

              user.serverTimeOffset = time - Date.now();

              if (
                Boolean(currentInstitution.id) &&
                (!Boolean(user.institutionMember) ||
                  !Boolean(user.institutionMember.active))
              ) {
                throw new Error(
                  'Current institution exists but user is not a member or institution is inactive'
                );
              }

              const { subscriptionType } =
                await User.prototype$getSubscriptionType({
                  skipInstitutionCheck: currentInstitution.isNull(),
                  id: user.id,
                }).$promise;

              user.subscriptionType = subscriptionType;

              LoopBackAuth.currentUserData = user;
              $ngRedux.dispatch(setUser(user));
              $ngRedux.dispatch(resetExtensionsState());
              $ngRedux.dispatch(loadAndInstallExtensions());

              updateGrowthBookIndentifiers({
                user,
                institution: currentInstitution,
              });

              return user;
            } catch (err) {
              if (err.status !== -1 && err.status !== 500) {
                Auth.clearCurrentUser();
                $state.go('signIn');
              }
            }
          },
          /* eslint-enable consistent-return */
          rooms: async (User, currentInstitution, $ngRedux) => {
            const userId = Number(User.getCurrentId());
            const institutionId =
              currentInstitution.id !== null ? currentInstitution.id : null;
            const activeRooms = await getActiveRooms(userId, institutionId);
            $ngRedux.dispatch(setRooms(activeRooms));

            return activeRooms;
          },

          async room(
            $rootScope,
            $q,
            rooms,
            user,
            currentInstitution,
            $ngRedux,
            Auth,
            roomHelpersFactory,
            $stateParams,
            logOutFactory
          ) {
            const roomId = Number.parseInt(
              localStorage.getItem(localStorageKeyForRoomId)
            );
            const currentRoom =
              Boolean(roomId) && rooms.find((_room) => _room.id === roomId);
            let room = Boolean(currentRoom)
              ? currentRoom
              : rooms.find((_room) => {
                  return _room.roomMemberships.find((roomMembership) => {
                    return (
                      roomMembership.role === 'admin' &&
                      roomMembership.userId === user.id
                    );
                  });
                });
            if (
              Boolean(currentInstitution.id) &&
              Boolean(currentInstitution.hasAccess('inviteFeatures_landing')) &&
              roomId === 'landing'
            ) {
              room = getLandingPage(currentInstitution);
            }

            const personalRoom = rooms.find((_room) => {
              return _room.roomMemberships.find((roomMembership) => {
                return (
                  roomMembership.role === 'admin' &&
                  roomMembership.userId === user.id
                );
              });
            });

            if (Boolean(room)) {
              $rootScope.$emit('currentRoomResolved', room);
              $ngRedux.dispatch(setRoom(room));

              return { ...personalRoom };
            }
            const activeInstitutionMembershipWithRedirect =
              user.institutionsMemberships?.find((membership) => {
                return (
                  Boolean(membership.active) &&
                  Boolean(membership.institution?.active) &&
                  Boolean(membership.institution?.shouldRedirect)
                );
              });
            if (
              !Boolean(room) &&
              Boolean($stateParams.afterLogin) &&
              !Boolean(currentInstitution.id) &&
              Boolean(activeInstitutionMembershipWithRedirect)
            ) {
              const { institution } = activeInstitutionMembershipWithRedirect;
              try {
                const url = roomHelpersFactory.getRoomBaseUrl(institution);
                await Auth.rememberInstitutionAuthState(institution);
                await logOutFactory.cleanUp();
                window.location.href = url;
              } catch (error) {
                console.error(error);
              }
            }
            return $q.reject({ target: 'createRoom' });
          },

          reduxSync($rootScope, $ngRedux, user) {
            const unsubscribeStore = $ngRedux.subscribe(() => {
              const state = $ngRedux.getState();
              angular.extend(user, selectUser(state));
            });
            const unsubscribeStateChange = $rootScope.$on(
              '$stateChangeSuccess',
              (event, toState) => {
                if (toState.name.indexOf('account.') !== 0) {
                  unsubscribeStateChange();
                  unsubscribeStore();
                }
              }
            );
          },

          personalData(externalDataFactory) {
            return externalDataFactory.get('personal');
          },

          purchases: (User, user, $ngRedux) => {
            const promise =
              user && user.id
                ? User.purchases({
                    id: user.id,
                    filter: { include: 'institution' },
                  }).$promise
                : Promise.resolve([]);

            promise.then((purchases) => {
              $ngRedux.dispatch(setPurchases([...purchases]));
            });

            return promise;
          },

          plans: (Purchase, $ngRedux) => {
            return Purchase.getPlans().$promise.then((plans) => {
              $ngRedux.dispatch(setPlans(plans));
              return plans;
            });
          },

          locale: ($translate, $stateParams, user) => {
            if ($stateParams.l) {
              $translate.use($stateParams.l);
            } else {
              const locale = verifySupportedLocale(
                PEOPLE_TYPES.doctor,
                user.preferredLocale || navigator.language
              );

              $translate.use(locale);
            }
            return $translate.onReady();
          },

          VideoProvider: (VideoProviderFactory) =>
            VideoProviderFactory.waitForActiveProvider(),

          loadCanny: (cannyFactory) => {
            try {
              cannyFactory.loadSDK();
            } catch {
              // Factory will have logged error.
            }
          },

          userManageableFeatures: async (
            $ngRedux,
            user,
            currentInstitution
          ) => {
            const features = await API.userManageableFeatures.getFeatures({
              userId: user.id,
              institutionId: Boolean(currentInstitution)
                ? currentInstitution.id
                : null,
            });
            const featuresAsObject = {};
            for (const feature of features) {
              featuresAsObject[feature.key] = feature.isEnabled;
            }
            $ngRedux.dispatch(setUserManageableFeatures(featuresAsObject));
          },
        },

        lazyLoad($transition$) {
          return import('../account/account').then(({ default: module }) => {
            $transition$.injector().native.loadNewModules([module]);
          });
        },
      })
      .state('account.dashboard', {
        url: '/dashboard?modal&survey',
        component: 'accountDashboard',
        data: { title: 'Dashboard' },
        params: {
          welcomePopup: null,
          cannyRedirectUrl: null,
          survey: null,
          accountType: null,
        },
        resolve: {
          v2DashboardRedirect(v2) {
            if (Boolean(v2.isEnabled())) {
              const accountType = new URLSearchParams(
                window.location.search
              ).get('accountType');

              v2.redirect(
                `/account/dashboard${
                  accountType ? `?accountType=${accountType}` : ''
                }`
              );
            }
          },
          afterV2Login($stateParams, $state) {
            const url = new URL(window.location.href);
            const params = new URLSearchParams(url.search);

            const isFromV2Login = params.has('fromLogin');
            if (isFromV2Login) {
              // Perform state update if conditions met which will remove the QS
              // eslint-disable-next-line unicorn/no-lonely-if
              if ($stateParams.afterLogin === false) {
                $state.go('account.dashboard', {
                  afterLogin: true,
                });
              }
            }
          },
        },
      })
      .state('account.waiting-room-edit', {
        url: '/waiting-room/edit/{id}',
        templateUrl:
          '/app/account/waiting-room-edit/account.waiting-room-edit.html',
        controller: 'WaitingRoomEditController as waitingRoom',
        data: { title: 'Waiting Room' },
        params: { hideBackButton: null, backTo: null },
        resolve: {
          room: function (Room, $stateParams) {
            return Room.findRoomById({
              id: $stateParams.id,
              include: ['widgets'],
            }).$promise;
          },
        },
      })
      .state('account.rooms', {
        url: '/rooms',
        templateUrl: '/app/account/shared-rooms/account.shared-rooms.tpl.html',
        controller: 'SharedRoomsController as vm',
        data: { title: 'Clinic' },
        resolve: {
          roomsMemberships: getRoomsMemberships,
        },
      })
      .state('authLinkedin', {
        url: '/auth/linkedin?{code}',
        template: `<div>
        Loading... Please wait... This window will be closed automatically
          <div class="root-page-loader">
            <svg viewBox="25 25 50 50">
              <circle cx="50" cy="50" r="21" fill="none" stroke-width="2" stroke-miterlimit="10"></circle>
            </svg>
          </div>
        </div>`,
        data: {
          access: {
            level: 'all',
          },
        },
        onEnter: function ($window, $stateParams, linkedInConfig, baseUrl) {
          const { clientId } = linkedInConfig;
          const linkedinRedirectUrl = `${baseUrl}/auth/linkedin`;
          if ($stateParams.code) {
            const { code } = $stateParams;
            $window.opener.postMessage({ code }, '*');
          } else {
            $window.location = `https://www.linkedin.com/oauth/v2/authorization/?response_type=code&client_id=${clientId}&scope=r_liteprofile&redirect_uri=${linkedinRedirectUrl}`;
          }
        },
      })
      .state('account.settings', {
        url: '/settings',
        abstract: true,
        component: 'accountSettings',
        data: { title: 'Settings', accessKey: 'account_settings' },
        resolve: {
          // eslint-disable-next-line consistent-return
          v2SettingsRedirect: function (v2, $location) {
            if (Boolean(v2.isEnabled())) {
              return v2.redirect($location.$$path);
            }
          },
          personalData: function (externalDataFactory, $ngRedux) {
            return externalDataFactory.get('personal').then((personal) => {
              $ngRedux.dispatch(setPersonalData(personal));
              return personal;
            });
          },

          states: function (externalDataFactory, $ngRedux) {
            return externalDataFactory.get('states').then((states) => {
              $ngRedux.dispatch(setStates(states));
              return states;
            });
          },

          locationsLicenced: function (User, user, $ngRedux) {
            return User.locationsLicenced({ id: user.id }).$promise.then(
              (locationsLicensed) => {
                $ngRedux.dispatch(setLocationsLicensed([...locationsLicensed]));
                return locationsLicensed;
              }
            );
          },
        },
      })
      .state('account.settings.profile', {
        url: '/profile?code',
        data: {
          accessKey: 'account_settings_personal_and_professional_info',
          redirectIfNoTo: 'account.settings.room',
        },
      })
      .state('account.settings.room', {
        url: '/room',
        data: {
          accessKey: 'account_roomFeatures_modifyRoomSettings',
          redirectIfNoTo: 'account.settings.account',
        },
      })
      .state('account.settings.account', {
        url: '/account',
        data: {
          accessKey: 'account_settings_general',
          redirectIfNoTo: 'account.settings.notifications',
        },
      })
      .state('account.settings.legal', {
        url: '/legal',
        data: {
          accessKey: 'account_settings_baa',
        },
      })
      .state('account.settings.notifications', {
        url: '/notifications',
        data: {
          redirectIfNoTo: 'account.settings.team',
        },
      })
      .state('account.settings.team', {
        url: '/team',
        templateUrl: '/app/account/settings/team/settings.team.tpl.html',
        controller: 'SettingsTeamController',
        data: {
          accessKey: 'account_settings_team',
          redirectIfNoTo: 'account.settings.extensions',
        },
      })
      .state('account.settings.extensions', {
        url: '/extensions',
        data: {
          accessKey: 'account_settings_extensions',
          redirectIfNoTo: 'account.settings.badges',
        },
      })
      .state('account.settings.badges', {
        url: '/badges',
        data: {
          accessKey: ['roomFeatures_createBadge', 'inviteFeatures_prints'],
        },
      })
      .state('account.admin', {
        url: '/admin',
        abstract: true,
        templateUrl: '/app/account/admin/account.admin.tpl.html',
        data: { title: 'Admin' },
        controller: 'AccountAdminController as $ctrl',
        lazyLoad($transition$) {
          return import('../account/admin/account.admin').then(
            ({ default: module }) => {
              $transition$.injector().native.loadNewModules([module]);
            }
          );
        },
      })
      .state('account.admin.users', {
        url: '/users',
        templateUrl: '/app/account/admin/users/admin.users.tpl.html',
        controller: 'AdminUsersController',
      })
      .state('account.admin.institutions', {
        url: '/institutions',
        templateUrl:
          '/app/account/admin/institutions/admin.institutions.tpl.html',
        controller: 'AdminInstitutionsController',
      })
      .state('account.admin.beta', {
        url: '/beta',
        templateUrl: '/app/account/admin/beta/admin.beta.tpl.html',
        controller: 'AdminBetaController as vm',
      })
      .state('account.admin.extensions', {
        url: '/extensions',
        abstract: true,
      })
      .state('account.admin.extensions.list', {
        url: '/list',
        component: ReactApp,
      })
      .state('account.admin.extensions.edit', {
        url: '/edit/:id',
        component: ReactApp,
      })
      .state('account.admin.extensions.create', {
        url: '/create',
        component: ReactApp,
      })
      .state('account.admin.extensions.statistics', {
        url: '/statistics/:id',
        component: ReactApp,
      })
      .state('account.admin.sso-services', {
        url: '/sso-services',
        abstract: true,
      })
      .state('account.admin.sso-services.list', {
        url: '/list',
        component: ReactApp,
      })
      .state('account.admin.sso-services.create', {
        url: '/create',
        component: ReactApp,
      })
      .state('account.admin.sso-services.edit', {
        url: '/edit/:id',
        component: ReactApp,
      })
      .state('account.admin.purchases', {
        url: '/purchases',
        templateUrl: '/app/account/admin/purchases/admin.purchases.tpl.html',
        controller: 'AdminPurchasesController as vm',
      })
      .state('account.admin.theme', {
        url: '/theme?institutionId',
        templateUrl: '/app/account/admin/theme/admin.theme.tpl.html',
        controller: 'AdminThemeController',
        resolve: {
          currentInstitution: getCurrentInstitution,
          institution: function (Institution, $stateParams) {
            return Institution.findById({ id: $stateParams.institutionId })
              .$promise;
          },
        },
      })
      .state('account.teleconsent', {
        url: '/extensions/teleconsent/:institutionId',
        params: {
          institutionId: null,
        },
        templateUrl:
          '/app/realtime/components/teleconsent/manager/teleconsent.manager.tpl.html',
        abstract: true,
        lazyLoad($transition$) {
          return import('../realtime/components/teleconsent/teleconsent').then(
            ({ default: module }) => {
              $transition$.injector().native.loadNewModules([module]);
            }
          );
        },
      })
      .state('account.teleconsent.list', {
        url: '/list',
        templateUrl:
          '/app/realtime/components/teleconsent/manager/list/' +
          'teleconsent.manager.list-page.tpl.html',
        controller: 'TCManagerListPageController as vm',
      })
      .state('account.teleconsent.paged-editor', {
        url: '/paged-editor?id',
        component: 'pagedEditor',
        resolve: {
          institutionId: ($stateParams, currentInstitution) => {
            return $stateParams.institutionId || currentInstitution.id;
          },
          document: (
            $stateParams,
            institutionId,
            Institution,
            TeleconsentDocument
          ) => {
            if (Boolean($stateParams.id)) {
              return TeleconsentDocument.findById({
                id: $stateParams.id,
              }).$promise;
            }
            return Promise.resolve(null);
          },
        },
        lazyLoad($transition$) {
          return import('../account/settings/extensions/teleconsent').then(
            ({ default: module }) => {
              $transition$.injector().native.loadNewModules([module]);
            }
          );
        },
      })
      .state('account.subscription', {
        url: '/subscription?modal',
        component: 'accountSubscription',
        data: { title: 'Subscriptions' },
        resolve: {
          plans: (Purchase) => Purchase.getPlans().$promise,
        },
      })
      .state('account.subscription.upgrade', {
        url: '/upgrade/{plan}',
        templateUrl:
          '/app/account/subscription/upgrade/account.subscription.upgrade.tpl.html',
        controller: 'AccountUpgradeController as vm',
        data: { title: 'Upgrade' },
        resolve: {
          defaultCard: (payment, User) => {
            // Current user may be empty on reload, make sure it's always there
            const currentUser = User.getCachedCurrent();
            if (!currentUser) {
              return User.getCurrent().$promise.then((user) =>
                payment.getDefaultCard(user)
              );
            } else {
              return payment.getDefaultCard(currentUser);
            }
          },
        },
      })
      .state('oauth', {
        url: '/oauth',
        template: '<ui-view/>',
        abstract: true,
        data: {
          access: {
            level: 'all',
          },
        },
      })
      .state('oauth.stripe', {
        url: '/stripe?{state}{code}{error}{error_description}',
        onEnter: function ($window, $stateParams) {
          const protocol = $window.location.protocol;
          const stateArr = decodeURIComponent($stateParams.state || '').split(
            ','
          );
          const institutionDomain = stateArr[0];
          const oAuthToken = stateArr[1] || '';
          const subDomain =
            institutionDomain && /^[a-z0-9-]+$/i.test(institutionDomain)
              ? institutionDomain + '.'
              : '';
          const queryParameters = new URLSearchParams();
          Object.keys($stateParams)
            .filter((k) => k !== 'state')
            .forEach((k) => {
              if ($stateParams[k]) {
                queryParameters.set(k, $stateParams[k]);
              }
            });
          if (oAuthToken) {
            queryParameters.set('tokenId', oAuthToken);
          }
          $window.location =
            protocol +
            '//' +
            subDomain +
            $window.location.host +
            '/account/extensions/payment/oauth?' +
            queryParameters.toString();
        },
      })
      .state('account.payment', {
        url: '/extensions/payment',
        component: 'paymentManager',
        abstract: true,
      })
      .state('account.payment.oauth', {
        url: '/oauth?{code}{tokenId}{error}{error_description}',
        component: 'accountPaymentOauth',
      })
      .state('patient', {
        url: '/:roomSlug?username&pid&vcp&l&autocheckin&token&transfer&fromRoomId&survey&passcode',
        component: 'patient',
        data: {
          access: {
            level: 'all',
          },
          robots: 'noindex,follow',
          title: 'Doxy.me - Virtual Waiting Room',
          description:
            'Click this link to have a telemedicine video visit with me',
        },
        params: {
          username: null,
          pid: null,
          vcp: null, // vcp = video chat provider (tokbox)
          l: null,
          token: null,
          autocheckin: null,
          fromRoomId: null,
          survey: null,
          passcode: null,
        },
        lazyLoad($transition$) {
          return import('../patient/patient').then(({ default: module }) => {
            $transition$.injector().native.loadNewModules([module]);
          });
        },
        resolve: {
          apiConfig: reactResolvers.apiConfig,
          currentInstitution: getCurrentInstitution,
          room(
            Room,
            $stateParams,
            currentInstitution,
            $log,
            $rootScope,
            $ngRedux,
            v2,
            $location
          ) {
            if ($stateParams.roomSlug === 'telemedicine') {
              return Promise.reject({ target: 'home' });
            }

            const queryParams = $location.search();

            return Room.findBySlug({
              slug: $stateParams.roomSlug,
              institutionId: currentInstitution.id || null,
              $_clearAuthorization: true,
            })
              .$promise.then((room) => {
                const adminUser = room.getAdminUser();
                $ngRedux.dispatch(
                  actions.initDokbot({
                    institutionId: currentInstitution.id || null,
                    userId: adminUser.id || null,
                  })
                );

                if (Boolean(room.shouldRedirect) && Boolean(room.redirectUrl)) {
                  window.location.href = room.redirectUrl;

                  // Return a promise that never resolves to prevent the state from being loaded
                  return wait(10000);
                }

                if (
                  Boolean(room.isV2Enabled) &&
                  !Boolean(currentInstitution.id) // Disable redirect for clinics until feature is released for clinics
                ) {
                  /*
                    Perform redirect to V2 Apollo check-in page if it's enabled
                    Value comes from back-end.
                    Prerequisites:
                    - Apollo Call toggle is enabled in provider's room settings
                    - Any V2 Check-in feature flag e.g. https://app.growthbook.io/features/corev2_start_call_redirect is enabled for that user
                  */
                  return v2.redirect(
                    `/check-in/${room.slug}`,
                    $stateParams.passcode,
                    $stateParams.l,
                    queryParams
                  );
                }

                $rootScope.$emit('currentRoomResolved', room);
                return room;
              })
              .catch((err) => {
                $log.info('patient', {
                  error: err,
                  action: 'patient',
                  room: { slug: $stateParams.roomSlug },
                });
                const status = _.get(err, 'status');
                return Promise.reject(
                  status !== -1 && status < 500
                    ? {
                        target: 'wrongRoom',
                        data: {
                          slug: $stateParams.roomSlug,
                          domain: currentInstitution.domain,
                        },
                      }
                    : {}
                );
              });
          },
          roomAdminCanUsePaidFeatures(roomAdminPlanType) {
            return ['trial', 'pro', 'clinic'].includes(roomAdminPlanType);
          },
          roomAdminPlanType(User, room, currentInstitution) {
            const adminUser = room.getAdminUser();
            if (adminUser.isNull()) {
              return currentInstitution.isNull() ? 'free' : 'clinic';
            }
            const skipInstitutionCheck = currentInstitution.isNull();
            return User.prototype$getSubscriptionType({
              id: adminUser.id,
              skipInstitutionCheck,
            }).$promise.then((result) => {
              return result.subscriptionType;
            });
          },
          locale: ($translate, $stateParams) => {
            if ($stateParams.l) {
              $translate.use($stateParams.l);
            } else {
              const locale = verifySupportedLocale(
                PEOPLE_TYPES.patient,
                navigator.language
              );

              $translate.use(locale);
            }
            return $translate.onReady();
          },
          VideoProvider: (VideoProviderFactory) =>
            VideoProviderFactory.waitForActiveProvider(),
        },
      })
      .state('oauth.basic', {
        url: '/authorize?client_id&state&response_type&approval_prompt&redirect_uri', // = /oauth/authorize
        component: 'oauthBasic',
        data: {
          access: {
            level: 'all',
          },
          title: 'Authorization request | Doxy.me',
        },
        resolve: {
          currentInstitution: getCurrentInstitution,
          user(User, LoopBackAuth) {
            if (!User.getCurrentId()) return Promise.resolve(null);
            return User.findById({
              id: User.getCurrentId(),
            }).$promise.then(function (user) {
              LoopBackAuth.currentUserData = user;
              return user;
            });
          },
        },
        lazyLoad($transition$) {
          return import('../home/oauth-basic/oauth-basic').then(
            ({ default: module }) => {
              $transition$.injector().native.loadNewModules([module]);
            }
          );
        },
      });

    $urlRouterProvider.rule(function ($injector, $location) {
      const path = $location.path();

      if (path.endsWith('/')) {
        const newPath = path.substring(0, path.length - 1);
        return newPath;
      }
    });

    $urlRouterProvider.otherwise(function ($injector) {
      $injector.get('$state').go('signIn');
    });

    $locationProvider.html5Mode(true);
  })
  .run(function ($rootScope, $transitions, trackingFactory) {
    $transitions.onSuccess({ from: 'account.marketplace' }, (trans) => {
      if (trans.$to().name !== 'account.extension') {
        $rootScope.$emit(RootScopeEvents.MARKETPLACE.CLOSED);
      }
    });
    $rootScope.$on(
      '$locationChangeStart',
      async function (event, next, current) {
        if (await shouldRedirectToBlacklist()) {
          event.preventDefault();
          redirectToBlacklistPage();
        }
      }
    );
    $rootScope.$on('$stateChangeSuccess', function (ev, to, toParams, from) {
      if (Boolean(from.url)) {
        trackingFactory.global.trackPageView();
      }

      $rootScope.previousState = from.name;
      $rootScope.currentState = to.name;
    });
  });
